import { useCallback, useEffect, useRef, useState } from 'react'

import { randomInRange, sortNumbers, multiplyNumbers } from '~/bootcamp/Activities/utils/activities.utils'

const NUMBER_SET_BY_DIFFICULTY: { [key: number]: { primesSet: number[][]; numberOfPrimeAnswers: number } } = {
  1: {
    primesSet: [
      [2, 3, 5],
      [2, 3, 5],
    ],
    numberOfPrimeAnswers: 2,
  },
  2: {
    primesSet: [
      [2, 3, 5, 7],
      [2, 3, 5],
      [2, 3],
    ],
    numberOfPrimeAnswers: 3,
  },
  3: {
    primesSet: [
      [2, 3, 5, 7],
      [2, 3, 5],
      [2, 3],
      [2, 3],
    ],
    numberOfPrimeAnswers: 4,
  },
  4: {
    primesSet: [
      [2, 3, 5, 7, 11],
      [2, 3, 5],
      [2, 3, 5],
      [2, 3],
      [2, 3],
    ],
    numberOfPrimeAnswers: 5,
  },
}

function choosePrimesFromSet(numberSet: number[][]): number[] {
  const primes = numberSet.map((numbers) => {
    const nSet = [...numbers]
    const choice = randomInRange(0, nSet.length)

    return nSet[choice]
  })

  return sortNumbers(primes)
}

function generateQuotients(compoundNumber: number, primeNumbers: number[]): number[] {
  let quotientNumbers: number[] = []
  let quotient = compoundNumber

  primeNumbers.forEach((prime) => {
    const result = quotient / prime

    quotientNumbers = [...quotientNumbers, result]
    quotient = result
  })

  return quotientNumbers
}

function generateFactorDivisionQuestion(primeNumbersSet: number[][]): {
  compound: number
  primes: number[]
  quotients: number[]
} {
  const primes = choosePrimesFromSet(primeNumbersSet)
  const compound = multiplyNumbers(primes)

  const quotients = generateQuotients(compound, primes)

  return {
    compound,
    primes,
    quotients,
  }
}

export default function useFactorDivision(
  difficultyLevel = 1,
): [number, number, number[], () => void, (answer: number[]) => boolean] {
  const [compoundNumber, setCompoundNumber] = useState<number>(0)
  const [primeNumbersList, setPrimeNumbersList] = useState<number[]>([])
  const [quotientsList, setQuotientsList] = useState<number[]>([])
  const taskHistory = useRef<number[]>([])
  const retry = useRef<number>(0)

  const generateNewQuestion = useCallback((): void => {
    const { compound, primes, quotients } = generateFactorDivisionQuestion(
      NUMBER_SET_BY_DIFFICULTY[difficultyLevel].primesSet,
    )

    if (!taskHistory.current.includes(compound) || retry.current >= 3) {
      taskHistory.current = [...taskHistory.current, compound].slice(-3)
      setCompoundNumber(compound)
      setPrimeNumbersList(primes)
      setQuotientsList(quotients)
      retry.current = 0
    } else {
      generateNewQuestion()
      retry.current += 1
    }
  }, [difficultyLevel])

  useEffect(() => {
    generateNewQuestion()
  }, [generateNewQuestion])

  const verifyAnswer = (answer: number[]): boolean => {
    return JSON.stringify(sortNumbers(answer)) === JSON.stringify(primeNumbersList)
  }

  return [
    compoundNumber,
    NUMBER_SET_BY_DIFFICULTY[difficultyLevel].numberOfPrimeAnswers,
    quotientsList,
    generateNewQuestion,
    verifyAnswer,
  ]
}
